import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import FileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AddTaskIcon from '@mui/icons-material/AddTask';
import CommentIcon from '@mui/icons-material/Comment';
import EndDayIcon from '@mui/icons-material/EventAvailable';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { apiGet, apiPost } from '../../utils/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useRef } from 'react';

const TASK_CATEGORIES = {
  Volumetrie: {
    'Lucrari noi introduse in CRM': null,
    'Telefoane Externe': ['Lucrari Existente', 'Lucrari Noi', 'Colaboratori Noi', 'Colaboratori Existenti', 'Alte Tipuri'],
    'Email-uri trimise': [
      'Intern Comenzi CRM',
      'Intern Solicitare Ofertă',
      'Intern Administrativ',
      'Intern Solicitare Ctr/comanda',
      'Intern Altele',
      'Intern Sesizari',
      'Intern Suport Tehnic',
      'Extern Ofertare',
      'Extern Prezentare',
      'Extern Concurență',
      'Extern Colaborare',
    ],
   
    Prospectie: ['Online','Soft', 'Teren'],
    Rapoarte: ['Activitate', 'Concurenta', 'Vanzari'],
    Intalniri: ['Prezentare', 'Discutare Oferta', 'Discutare Colaborare', 'Inchidere Ctr'],
    Sedinte: ['Training', 'Discutii specifice', 'Generale (de firma)'],
    Altele: null,
  },
  Rezultate: {
    'Contracte de colaborare noi': [
      'Prospectie Online',
      'Teren',
      'Clienți mulțumiți',
      'Cunoștințe',
      'Alta sursă',
      'Soft',
    ],
    'Contacte Noi Abordate': null,
    'Proiecte noi': [
      'Colaborator existent',
      'Colaborator nou',
      'Client mulțumit',
      'Cunoștințe',
      'Soft',
    ],
    Prospectie: ['Teren', 'Online'],
  },
  'Amprenta Personala': {
    'Informatii Concurenta (Brand / Sistem)': null,
    'Obstacole Intampinate': null,
    'Feedback client ( Negativ / Pozitiv )': null,
    'Propuneri si Idei': null,
  },
  'Observatii Manager': {
    'Observatii': null,
    'Raspunsuri': null,
  },
};

interface Task {
  id: number;
  category: string;
  name: string;
  type: string;
  quantity: number;
  time: number;
  comments: string;
  lastModifiedBy?: number;
  lastModifiedAt?: string;
  userId?: number; // Added userId for custom tasks
}

interface User {
  id: number;
  username: string;
}

interface TaskFile {
  id: number;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  userId: number;
  createdAt: string;
}

interface TaskChangeHistory {
  id: number;
  taskId: number;
  changedByUserId: number;
  fieldName: string;
  previousValue: string | null;
  newValue: string | null;
  taskDate: string;
  userId: number;
  createdAt: string;
  changedByUser?: {
    id: number;
    username: string;
  };
  task?: {
    id: number;
    category: string;
    name: string;
    type: string;
  };
}

const TaskManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskType, setNewTaskType] = useState('');
  const [addTaskError, setAddTaskError] = useState('');

  // State for delete confirmation
  const [deleteTaskId, setDeleteTaskId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // State for file upload
  const [files, setFiles] = useState<TaskFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);

  // State for past task editing permission
  const [canEditPastTasks, setCanEditPastTasks] = useState(false);
  useEffect(() => {
    apiGet('/task/allow-past-edit').then(res => {
      setCanEditPastTasks(res.allowed);
    }).catch(() => setCanEditPastTasks(false));
  }, []);

  // State for change history
  const [changeHistory, setChangeHistory] = useState<TaskChangeHistory[]>([]);
  const [changeHistoryLoading, setChangeHistoryLoading] = useState(false);

  // State for dynamic manager observation rows
  const [managerObservations, setManagerObservations] = useState<Task[]>([]);
  const [nextObservationId, setNextObservationId] = useState(1000); // Start with high ID to avoid conflicts

  // State for End Day modal
  const [endDayModalOpen, setEndDayModalOpen] = useState(false);
  const [endDayAnswers, setEndDayAnswers] = useState({
    crmIntroduced: '',
    newClientsCalled: '',
    expiredReminders: ''
  });
  const [endDayDates, setEndDayDates] = useState({
    crmIntroduced: null as Date | null,
    newClientsCalled: null as Date | null,
    expiredReminders: null as Date | null
  });
  const [endDayOccurrences, setEndDayOccurrences] = useState({
    crmIntroduced: 0,
    newClientsCalled: 0,
    expiredReminders: 0
  });
  const [endDayLoading, setEndDayLoading] = useState(false);

  // Add state for Observatii usernames
  const [observatiiUsernames, setObservatiiUsernames] = useState<{ [id: number]: string }>({});

  // Fetch users in hierarchy and set default to current user
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userList: User[] = await apiGet('/auth/users/hierarchy');
        setUsers(userList);
        
        // Check URL parameters for date and userId
        const urlDate = searchParams.get('date');
        const urlUserId = searchParams.get('userId');
        
        if (urlDate) {
          try {
            const [day, month, year] = urlDate.split('-').map(Number);
            const parsedDate = new Date(year, month - 1, day);
            setSelectedDate(parsedDate);
          } catch (error) {
            console.error('Error parsing date from URL:', error);
          }
        }
        
        if (urlUserId && userList.length > 0) {
          const userId = parseInt(urlUserId);
          const userExists = userList.find(u => u.id === userId);
          if (userExists) {
            setSelectedUserId(userId);
          } else if (selectedUserId === null) {
            setSelectedUserId(userList[0].id);
          }
        } else if (userList.length > 0 && selectedUserId === null) {
          setSelectedUserId(userList[0].id);
        }
      } catch (err) {
        setUsers([]);
      }
    };
    fetchUsers();
  }, [searchParams]);

  // Fetch current user for comparison
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const user = await apiGet('/auth/me');
        setCurrentUserId(user.id);
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };
    fetchCurrentUser();
  }, []);

  // Auto-fill effect only on user/date change or page load
  useEffect(() => {
    if (!selectedDate || !currentUserId || selectedUserId !== currentUserId) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    if (selected.getTime() !== today.getTime()) return;
    if (tasks.length === 0) return;
    apiGet('/client/count/created-today').then(res => {
      const crmTask = tasks.find(task => task.category === 'Volumetrie' && task.name === 'Lucrari noi introduse in CRM');
      if (!crmTask) return;
      if (crmTask.quantity !== res.count) {
        setTasks(prevTasks => prevTasks.map(task =>
          task.category === 'Volumetrie' && task.name === 'Lucrari noi introduse in CRM'
            ? { ...task, quantity: res.count }
            : task
        ));
        setHasChanges(true);
      }
    });
  }, [selectedDate, currentUserId, selectedUserId, tasks]);

  const generateDefaultTasks = (): Task[] => {
    const defaultTasks: Task[] = [];
    let taskId = 1;

    Object.entries(TASK_CATEGORIES).forEach(([category, categoryData]) => {
      // Skip only the Observatii Manager category
      if (category === 'Observatii Manager') {
        return;
      }
      
      Object.entries(categoryData).forEach(([name, types]) => {
        // Create one row per task name, regardless of types
        defaultTasks.push({
          id: taskId++,
          category,
          name,
          type: '',
          quantity: 0,
          time: 0,
          comments: '',
        });
      });
    });

    return defaultTasks;
  };

  const getAvailableTypes = useCallback((category: string, name: string, allTasks: Task[], currentTaskId?: number): string[] => {
    const categoryData = TASK_CATEGORIES[category as keyof typeof TASK_CATEGORIES];
    if (!categoryData) return [];
    
    const nameData = categoryData[name as keyof typeof categoryData];
    if (Array.isArray(nameData)) {
      // Get all types that are already selected by other tasks with the same category and name
      const selectedTypes = allTasks
        .filter(task => 
          task.category === category && 
          task.name === name && 
          task.id !== currentTaskId && 
          task.type !== ''
        )
        .map(task => task.type);
      
      // Filter out already selected types and sort
      return [...nameData]
        .filter(type => !selectedTypes.includes(type))
        .sort();
    }
    return [];
  }, []);





  const fetchTasks = async (date: Date, showLoading: boolean = true, userIdOverride?: number) => {
    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    setHasChanges(false);
    try {
      const formattedDate = format(date, 'dd-MM-yyyy');
      let url = `/task/hierarchy/${formattedDate}`;
      const userId = userIdOverride ?? selectedUserId;
      if (userId) {
        url += `?userId=${userId}`;
      }
      const data = await apiGet(url);
      
      // Separate regular tasks from manager observations
      const regularTasks = data.filter((task: Task) => task.category !== 'Observatii Manager');
      const managerObs = data.filter((task: Task) => task.category === 'Observatii Manager');
      
      // Always sort the fetched data to maintain consistent order
      const sortedData = sortTasksByCategoryOrder(regularTasks);
      setTasks(sortedData);
      setManagerObservations(managerObs);
      
      // Update next observation ID to avoid conflicts
      if (managerObs.length > 0) {
        const maxId = Math.max(...managerObs.map((t: Task) => t.id));
        setNextObservationId(maxId + 1);
      }
      
      // If the response is empty, immediately save default tasks
      if (data.length === 0) {
        const defaultTasks = generateDefaultTasks();
        const sortedDefaultTasks = sortTasksByCategoryOrder(defaultTasks);
        const tasksData = sortedDefaultTasks.map(task => ({
          task_date: formattedDate,
          category: task.category,
          name: task.name,
          type: task.type,
          quantity: task.quantity,
          time: task.time,
          comments: task.comments,
          userId: userId,
        }));
        
        await apiPost('/task/bulk', tasksData);
        // Refetch tasks for the selected user and date to update the UI
        await fetchTasks(date, false, userId ?? undefined);
      }
    } catch (err: any) {
      setError(err.message);
      setTasks([]);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const sortTasksByCategoryOrder = (tasksToSort: Task[]): Task[] => {
    // Create a map of category and task name to their order in TASK_CATEGORIES
    const orderMap = new Map<string, number>();
    let orderIndex = 0;
    
    Object.entries(TASK_CATEGORIES).forEach(([category, categoryData]) => {
      Object.entries(categoryData).forEach(([name, types]) => {
        const key = `${category}|${name}`;
        orderMap.set(key, orderIndex++);
      });
    });
    
    return tasksToSort
      .map(task => ({
        ...task,
        // Sort the type alphabetically for display
        type: task.type || ''
      }))
      .sort((a, b) => {
        const keyA = `${a.category}|${a.name}`;
        const keyB = `${b.category}|${b.name}`;
        const orderA = orderMap.get(keyA) ?? Number.MAX_SAFE_INTEGER;
        const orderB = orderMap.get(keyB) ?? Number.MAX_SAFE_INTEGER;
        
        // If same category and name, sort by type alphabetically
        if (orderA === orderB) {
          // Put empty types at the end
          if (!a.type && b.type) return 1;
          if (a.type && !b.type) return -1;
          if (!a.type && !b.type) return 0;
          
          return a.type.localeCompare(b.type);
        }
        
        return orderA - orderB;
      });
  };

  const sortManagerObservations = (observations: Task[]): Task[] => {
    return observations.sort((a, b) => {
      // First sort by type (user ID) numerically
      const typeA = parseInt(a.type) || 0;
      const typeB = parseInt(b.type) || 0;
      if (typeA !== typeB) {
        return typeA - typeB;
      }
      
      // Then sort by name (Observatii before Raspunsuri)
      return a.name.localeCompare(b.name);
    });
  };

  // Show default tasks if no tasks found, and sort them by the original order
  const allTasks = useMemo(() => {
    return sortTasksByCategoryOrder(
      tasks.length > 0 ? tasks : generateDefaultTasks()
    );
  }, [tasks]);

  const saveAllTasks = useCallback(async (isTypeChange: boolean = false) => {
    if (!selectedDate || !selectedUserId) return;
    
    // Only show updating state for type changes to prevent UI refresh
    if (isTypeChange) {
      setUpdating(true);
    }
    setError(null);
    
    try {
      const formattedDate = format(selectedDate, 'dd-MM-yyyy');
      
      // Combine regular tasks and manager observations
      const allTasksToSave = [...allTasks, ...managerObservations];
      
      const tasksData = allTasksToSave.map(task => ({
        task_date: formattedDate,
        category: task.category,
        name: task.name,
        type: task.type,
        quantity: task.quantity,
        time: task.time,
        comments: task.comments,
        userId: selectedUserId,
      }));

      await apiPost(`/task/bulk?userId=${selectedUserId}`, tasksData);
      setHasChanges(false);
      
      // Only refetch if it's a type change (needed for dropdown availability updates)
      if (isTypeChange) {
        await fetchTasks(selectedDate, false, selectedUserId ?? undefined);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      if (isTypeChange) {
        setUpdating(false);
      }
    }
  }, [selectedDate, allTasks, managerObservations, selectedUserId]);
  
  // Filter out tasks that have no available types to select from
  const displayTasks = useMemo(() => {
    return allTasks.filter(task => {
      const categoryData = TASK_CATEGORIES[task.category as keyof typeof TASK_CATEGORIES];
      if (!categoryData) return false;
      
      const nameData = categoryData[task.name as keyof typeof categoryData];
      
      // If the task has no types array (null), always show it
      if (nameData === null) {
        return true;
      }
      
      // If the task has types array, check if there are available types
      if (Array.isArray(nameData)) {
        const availableTypes = getAvailableTypes(task.category, task.name, allTasks, task.id);
        return availableTypes.length > 0 || task.type !== '';
      }
      
      return false;
    });
  }, [allTasks, getAvailableTypes]);

  // Calculate total time for the summary row
  const totalTime = useMemo(() => {
    return displayTasks.reduce((sum, task) => sum + task.time, 0);
  }, [displayTasks]);
  const totalMinutes = totalTime;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const timeDisplay = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

  // Memoize available types for all tasks to avoid recalculating on every render
  const availableTypesMap = useMemo(() => {
    const map = new Map<string, string[]>();
    displayTasks.forEach(task => {
      const key = `${task.category}-${task.name}-${task.id}`;
      const availableTypes = getAvailableTypes(task.category, task.name, allTasks, task.id);
      map.set(key, availableTypes);
    });
    return map;
  }, [displayTasks, allTasks, getAvailableTypes]);

  // Refetch tasks when selected user or date changes
  useEffect(() => {
    if (selectedDate && selectedUserId !== null) {
      fetchTasks(selectedDate, true, selectedUserId);
    }
  }, [selectedDate, selectedUserId]);

  // Fetch files when date or user changes
  useEffect(() => {
    fetchFiles();
  }, [selectedDate, selectedUserId]);

  // Fetch change history when date or user changes
  useEffect(() => {
    if (selectedDate && selectedUserId !== null) {
      fetchChangeHistory();
    }
  }, [selectedDate, selectedUserId]);

  // Auto-save when changes are made
  useEffect(() => {
    if (hasChanges) {
      const timeoutId = setTimeout(() => {
        saveAllTasks(false); // Not a type change, so no refetch needed
      }, 500); // Auto-save after 2 seconds of no changes

      return () => clearTimeout(timeoutId);
    }
  }, [hasChanges, saveAllTasks]);

  // Delete handler
  const handleDeleteTask = async () => {
    if (!deleteTaskId) return;
    setDeleteLoading(true);
    try {
      await apiPost(`/task/${deleteTaskId}`, {}, 'DELETE');
      setDeleteTaskId(null);
      await fetchTasks(selectedDate!, false, selectedUserId ?? undefined);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };


  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  // Check if the selected date is in the past (before today)
  const isPastDate = (date: Date | null): boolean => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0); // Reset time to start of day
    return selectedDate < today;
  };

  const isEditingDisabled = (task: Task) => {
    // Always allow editing if past edit is enabled
    if (!isPastDate(selectedDate) || canEditPastTasks) {
      return false;
    }
    
    // For past dates, allow managers to edit Observatii and Raspunsuri tasks
    if (task.category === 'Observatii Manager' && (task.name === 'Observatii' || task.name === 'Raspunsuri')) {
      return false;
    }
    
    return true;
  };

  const handleOpenAddTaskModal = () => {
    setNewTaskName('');
    setNewTaskType('');
    setAddTaskError('');
    setAddTaskModalOpen(true);
  };

  const handleCloseAddTaskModal = () => {
    setAddTaskModalOpen(false);
    setAddTaskError('');
  };

  const handleAddCustomTask = async () => {
    if (!newTaskName.trim()) {
      setAddTaskError('Introduceți un nume pentru task.');
      return;
    }
    if (!selectedDate || !selectedUserId) return;
    const formattedDate = format(selectedDate, 'dd-MM-yyyy');
    const taskData = {
      task_date: formattedDate,
      category: 'Volumetrie',
      name: 'Altele',
      type: newTaskName.trim(),
      quantity: 0,
      time: 0,
      comments: '',
    };
    try {
      await apiPost(`/task?userId=${selectedUserId}`, taskData);
      await fetchTasks(selectedDate, false, selectedUserId);
      handleCloseAddTaskModal();
    } catch (err: any) {
      setAddTaskError(err.message);
    }
  };

  // End Day modal handlers
  const handleOpenEndDayModal = () => {
    setEndDayAnswers({
      crmIntroduced: '',
      newClientsCalled: '',
      expiredReminders: ''
    });
    setEndDayDates({
      crmIntroduced: null,
      newClientsCalled: null,
      expiredReminders: null
    });
    setEndDayOccurrences({
      crmIntroduced: 0,
      newClientsCalled: 0,
      expiredReminders: 0
    });
    setEndDayModalOpen(true);
  };

  const handleCloseEndDayModal = () => {
    setEndDayModalOpen(false);
  };

  const handleEndDaySubmit = async () => {
    // Validate that all questions are answered
    if (!endDayAnswers.crmIntroduced || !endDayAnswers.newClientsCalled || !endDayAnswers.expiredReminders) {
      alert('Vă rugăm să răspundeți la toate întrebările.');
      return;
    }

    // Validate that dates and occurrences are provided when answer is 'DA'
    if (endDayAnswers.crmIntroduced === 'DA' && (!endDayDates.crmIntroduced || endDayOccurrences.crmIntroduced <= 0)) {
      alert('Pentru "Toti clientii noi au fost introdusi in CRM" - DA, vă rugăm să introduceți data și numărul de apariții.');
      return;
    }
    if (endDayAnswers.newClientsCalled === 'DA' && (!endDayDates.newClientsCalled || endDayOccurrences.newClientsCalled <= 0)) {
      alert('Pentru "Clienti noi neapelati" - DA, vă rugăm să introduceți data și numărul de apariții.');
      return;
    }
    if (endDayAnswers.expiredReminders === 'DA' && (!endDayDates.expiredReminders || endDayOccurrences.expiredReminders <= 0)) {
      alert('Pentru "Exista remindere expirate" - DA, vă rugăm să introduceți data și numărul de apariții.');
      return;
    }

    setEndDayLoading(true);
    try {
      // Update the Restante tasks based on the answers
      const updatedTasks = [...tasks];
      let nextTaskId = Math.max(...tasks.map(t => t.id), 0) + 1;
      
      // Define the Restante task types and their mappings
      const restanteTypes = [
        'Clienti Noi Neintrodusi CRM',
        'Clienti noi necontactati', 
        'Remindere Necontactate'
      ];
      
      // Check if Restante tasks exist, if not create them
      restanteTypes.forEach(type => {
        const existingTask = updatedTasks.find(task => 
          task.category === 'Volumetrie' && 
          task.name === 'Restante' && 
          task.type === type
        );
        
        if (!existingTask) {
          // Create new Restante task
          const newTask: Task = {
            id: nextTaskId++,
            category: 'Volumetrie',
            name: 'Restante',
            type: type,
            quantity: 0,
            time: 0,
            comments: '',
          };
          updatedTasks.push(newTask);
        }
      });
      
      // Now update all Restante tasks with proper type mapping
      updatedTasks.forEach(task => {
        if (task.category === 'Volumetrie' && task.name === 'Restante') {
          // Map the answers to the correct Restante types and update quantities
          if (endDayAnswers.crmIntroduced === 'DA' && task.type === 'Clienti Noi Neintrodusi CRM') {
            task.quantity = endDayOccurrences.crmIntroduced;
            task.comments = `Data rezolvare: ${endDayDates.crmIntroduced ? format(endDayDates.crmIntroduced, 'dd.MM.yyyy') : ''}`;
          }
          if (endDayAnswers.newClientsCalled === 'DA' && task.type === 'Clienti noi necontactati') {
            task.quantity = endDayOccurrences.newClientsCalled;
            task.comments = `Data rezolvare: ${endDayDates.newClientsCalled ? format(endDayDates.newClientsCalled, 'dd.MM.yyyy') : ''}`;
          }
          if (endDayAnswers.expiredReminders === 'DA' && task.type === 'Remindere Necontactate') {
            task.quantity = endDayOccurrences.expiredReminders;
            task.comments = `Data rezolvare: ${endDayDates.expiredReminders ? format(endDayDates.expiredReminders, 'dd.MM.yyyy') : ''}`;
          }
          
          // Set quantity to 0 for tasks that were answered with 'NU'
          if (endDayAnswers.crmIntroduced === 'NU' && task.type === 'Clienti Noi Neintrodusi CRM') {
            task.quantity = 0;
            task.comments = 'Nu există clienti noi neintroduși în CRM';
          }
          if (endDayAnswers.newClientsCalled === 'NU' && task.type === 'Clienti noi necontactati') {
            task.quantity = 0;
            task.comments = 'Toți clienții noi au fost contactați';
          }
          if (endDayAnswers.expiredReminders === 'NU' && task.type === 'Remindere Necontactate') {
            task.quantity = 0;
            task.comments = 'Nu există remindere expirate';
          }
        }
      });
      
      // Update the tasks state
      setTasks(updatedTasks);
      setHasChanges(true);
      
      // Save the updated tasks directly to the backend since they include the new Restante tasks
      try {
        const formattedDate = format(selectedDate!, 'dd-MM-yyyy');
        
        // Combine updated tasks and manager observations
        const allTasksToSave = [...updatedTasks, ...managerObservations];
        
        const tasksData = allTasksToSave.map(task => ({
          task_date: formattedDate,
          category: task.category,
          name: task.name,
          type: task.type,
          quantity: task.quantity,
          time: task.time,
          comments: task.comments,
          userId: selectedUserId,
        }));

        await apiPost(`/task/bulk?userId=${selectedUserId}`, tasksData);
        setHasChanges(false);
        
        alert('End Day completat cu succes! Sarcinile Restante au fost actualizate cu tipurile și cantitățile corespunzătoare.');
        handleCloseEndDayModal();
      } catch (saveError: any) {
        console.error('Error saving tasks:', saveError);
        alert('Eroare la salvarea sarcinilor. Vă rugăm să încercați din nou.');
      }
    } catch (error) {
      console.error('Error submitting end day:', error);
      alert('Eroare la completarea End Day.');
    } finally {
      setEndDayLoading(false);
    }
  };

  // Cache for usernames not in users list
  const [externalUsernames, setExternalUsernames] = useState<{ [id: number]: string }>({});

  // Helper to get username for a userId, fetching from backend if needed
  const getUsername = useCallback(async (userId: number) => {
    const local = users.find(u => u.id === userId);
    if (local) return local.username;
    if (externalUsernames[userId]) return externalUsernames[userId];
    try {
      const res = await apiGet(`/auth/user/${userId}`);
      setExternalUsernames(prev => ({ ...prev, [userId]: res.username }));
      return res.username;
    } catch {
      return `Utilizator ID ${userId}`;
    }
  }, [users, externalUsernames]);

  // For rendering, use a state to store the username for tooltips
  const [hoveredTaskUser, setHoveredTaskUser] = useState<{ id: number, username: string } | null>(null);

  // Export to PDF handler
  const handleExportPDF = async () => {
    const tableElement = document.getElementById('task-table-export');
    if (!tableElement) return;
    const canvas = await html2canvas(tableElement, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pageWidth - 40;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, 'PNG', 20, 40, pdfWidth, pdfHeight);
    pdf.save(`task-management-${selectedUserId ?? 'user'}-${selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''}.pdf`);
  };

  // File upload functions
  const fetchFiles = async () => {
    if (!selectedDate || !selectedUserId) return;
    
    try {
      const formattedDate = format(selectedDate, 'dd-MM-yyyy');
      const timestamp = new Date().getTime();
      const response = await fetch(`/api/task/files?date=${formattedDate}&userId=${selectedUserId}&t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch files');
      }

      const data = await response.json();
      setFiles(data);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const fetchChangeHistory = async () => {
    if (!selectedDate || !selectedUserId) return;
    const formattedDate = format(selectedDate, 'dd-MM-yyyy');
    setChangeHistoryLoading(true);
    try {
      const history = await apiGet(`/task/change-history/date/${formattedDate}?userId=${selectedUserId}`);
      setChangeHistory(history);
    } catch (error) {
      setChangeHistory([]);
    } finally {
      setChangeHistoryLoading(false);
    }
  };

  const handleAddManagerObservation = async () => {
    const newObservationId = nextObservationId;
    const newAnswerId = nextObservationId + 1;
    
    const newObservation: Task = {
      id: newObservationId,
      category: 'Observatii Manager',
      name: 'Observatii',
      type: currentUserId?.toString() || '',
      quantity: 0,
      time: 0,
      comments: '',
    };

    const newAnswer: Task = {
      id: newAnswerId,
      category: 'Observatii Manager',
      name: 'Raspunsuri',
      type: currentUserId?.toString() || '',
      quantity: 0,
      time: 0,
      comments: '',
    };

    setManagerObservations(prev => [...prev, newObservation, newAnswer]);
    setNextObservationId(newAnswerId + 1);
    setHasChanges(true);

    // Send notification to the selected user
    if (selectedUserId && selectedUserId !== currentUserId) {
      try {
        const currentUser = users.find(u => u.id === currentUserId);
        const selectedUser = users.find(u => u.id === selectedUserId);
        
        if (currentUser && selectedUser) {
          await apiPost('/notification/send', {
            recipientId: selectedUserId,
            message: `${currentUser.username} a adăugat o observație manager pentru ${format(selectedDate!, 'dd.MM.yyyy')}`,
            url: `/task-management?date=${format(selectedDate!, 'dd-MM-yyyy')}&userId=${selectedUserId}`,
          });
        }
      } catch (error) {
        console.error('Error sending notification:', error);
      }
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedDate || !selectedUserId) return;

    setUploading(true);
    setFileUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const formattedDate = format(selectedDate, 'dd-MM-yyyy');
      const response = await fetch(`/api/task/upload?date=${formattedDate}&userId=${selectedUserId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload error response:', errorText);
        throw new Error('Upload failed');
      }

      const uploadResult = await response.json();

      await fetchFiles(); // Refresh file list
    } catch (error) {
      setFileUploadError('Eroare la încărcarea fișierului');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleFileDownload = async (filename: string, originalName: string) => {
    try {
      // If selectedUserId is different from currentUserId, pass it as query parameter
      const url = selectedUserId && selectedUserId !== currentUserId 
        ? `/api/task/files/${filename}?userId=${selectedUserId}`
        : `/api/task/files/${filename}`;
        
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url2 = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url2;
      a.download = originalName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url2);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      alert('Eroare la descărcarea fișierului');
    }
  };

  const handleFileDelete = async (filename: string) => {
    if (!window.confirm('Sigur doriți să ștergeți acest fișier?')) return;

    try {
      const response = await fetch(`/api/task/files/${filename}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      await fetchFiles(); // Refresh file list
    } catch (error) {
      console.error('Delete error:', error);
      alert('Eroare la ștergerea fișierului');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h5" fontWeight={700}>
             Oglinda Activitate
            </Typography>
            <Box display="flex" gap={3}>
              {/* Left column - Controls */}
              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" alignItems="center" gap={2}>
                  {hasChanges && (
                    <Typography variant="body2" color="warning.main">
                      Unsaved changes
                    </Typography>
                  )}
                  {updating && (
                    <CircularProgress size={20} />
                  )}
                </Box>
                <FormControl size="small" sx={{ 
                  minWidth: 180,
                  '& .MuiInputBase-input.Mui-disabled': {
                    color: '#666666',
                    WebkitTextFillColor: '#666666'
                  }
                }}>
                  <Select
                    value={selectedUserId ?? ''}
                    onChange={e => setSelectedUserId(Number(e.target.value))}
                    displayEmpty
                  >
                    {users.map(user => (
                      <MenuItem key={user.id} value={user.id}>{user.username}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                  <DatePicker
                    label="Select Date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { minWidth: 200 },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
              
              {/* Right column - Buttons */}
              <Box display="flex" flexDirection="column" gap={1}>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={handleExportPDF} 
                  startIcon={<PictureAsPdfIcon />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Export to PDF
                </Button>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={handleOpenAddTaskModal}
                  startIcon={<AddTaskIcon />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Adaugă task personalizat
                </Button>
                <Button 
                  variant="contained" 
                  size="small" 
                  onClick={handleOpenEndDayModal}
                  startIcon={<EndDayIcon />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  End Day
                </Button>
                {selectedUserId !== currentUserId && (
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={handleAddManagerObservation}
                    startIcon={<CommentIcon />}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Adauga observatie manager
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {/* Last updated info above the table */}
      <LastUpdatedInfo tasks={tasks} users={users} getUsername={getUsername} />
      {/* Tasks Table */}
      <Card>
        <CardContent>
          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isPastDate(selectedDate) && !canEditPastTasks && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Nu puteți edita sarcinile din trecut. Această dată este în trecut.
            </Alert>
          )}

          {!loading && (
            <TableContainer component={Paper}>
              <Table id="task-table-export" sx={{ borderCollapse: 'collapse' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ p: '2px' }}>Categorie123</TableCell>
                    <TableCell sx={{ p: '2px' }}>Task</TableCell>
                    <TableCell sx={{ p: '2px' }}>Tip</TableCell>
                    <TableCell sx={{ p: '2px' }}>Cantitate</TableCell>
                    <TableCell sx={{ p: '2px' }}>Timp Total</TableCell>
                    <TableCell sx={{ p: '2px' }}>Observatii</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.length === 0 && managerObservations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No tasks found for this date
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {/* Regular tasks */}
                      {tasks.map((task, index) => {
                      const key = `${task.category}-${task.name}-${task.id}`;
                      const availableTypes = availableTypesMap.get(key) || [];
                      
                      // Check if quantity is set but time is not (validation warning)
                      const hasQuantityButNoTime = task.quantity > 0 && task.time === 0;
                      
                      // Define background color and borders based on category
                      const getRowStyle = (): React.CSSProperties => {
                        const baseStyle: React.CSSProperties = {
                          border: 'none',
                        };
                        
                        // If validation warning, use red background
                        if (hasQuantityButNoTime) {
                          return { 
                            ...baseStyle,
                            backgroundColor: '#ffebee' // light red
                          };
                        }
                        
                        // Otherwise use normal category colors
                        switch (task.category) {
                          case 'Volumetrie':
                            return { 
                              ...baseStyle,
                              backgroundColor: '#f5f5f5' // light gray
                            };
                          case 'Rezultate':
                            return { 
                              ...baseStyle,
                              backgroundColor: '#f0f8f0' // light green
                            };
                          case 'Amprenta Personala':
                            return { 
                              ...baseStyle,
                              backgroundColor: '#f0f8ff' // light blue
                            };
                          default:
                            return baseStyle;
                        }
                      };
                      
                      // Define border styles for category and task cells
                      const getCategoryCellStyle = () => {
                        const baseStyle = getRowStyle();
                        const isLastOfCategory = index === tasks.length - 1 || tasks[index + 1].category !== task.category;
                        
                        return {
                          ...baseStyle,
                          borderBottom: isLastOfCategory ? '2px solid #ccc' : 'none',
                          borderRight: '1px solid #e0e0e0',
                          fontWeight: 700,
                        };
                      };
                      
                      const getTaskCellStyle = () => {
                        const baseStyle = getRowStyle();
                        const isLastOfTask = index === tasks.length - 1 || 
                          tasks[index + 1].category !== task.category || 
                          tasks[index + 1].name !== task.name;
                        
                        return {
                          ...baseStyle,
                          borderBottom: isLastOfTask ? '2px solid #ccc' : 'none',
                          borderRight: '1px solid #e0e0e0',
                          fontWeight: 700,
                        };
                      };
                      
                      // Check if this is the first task of its category
                      const isFirstOfCategory = index === 0 || tasks[index - 1].category !== task.category;
                      
                      // Check if this is the first task of its name within the category
                      const isFirstOfTaskName = isFirstOfCategory || tasks[index - 1].name !== task.name;
                      
                                              return (
                          <TableRow 
                            key={task.id} 
                            sx={getRowStyle()}
                          >
                            <TableCell sx={{ ...getCategoryCellStyle(), p: '2px' }}>
                              {isFirstOfCategory ? task.category : ''}
                            </TableCell>
                            <TableCell sx={{ ...getTaskCellStyle(), p: '2px' }}>
                              {isFirstOfTaskName ? task.name : ''}
                              {task.lastModifiedBy && currentUserId && task.lastModifiedBy !== currentUserId && (
                                <Tooltip
                                  title={`Ultima modificare: ${(hoveredTaskUser && hoveredTaskUser.id === task.lastModifiedBy) ? hoveredTaskUser.username : (users.find(u => u.id === task.lastModifiedBy)?.username || `Utilizator ID ${task.lastModifiedBy}`)} la ${task.lastModifiedAt ? format(new Date(task.lastModifiedAt), 'dd.MM.yyyy HH:mm') : ''}`}
                                  placement="top"
                                  onOpen={async () => {
                                    if (typeof task.lastModifiedBy === 'number' && !users.find(u => u.id === task.lastModifiedBy)) {
                                      const username = await getUsername(task.lastModifiedBy);
                                      setHoveredTaskUser({ id: task.lastModifiedBy, username });
                                    }
                                  }}
                                  onClose={() => setHoveredTaskUser(null)}
                                >
                                  <span><InfoOutlinedIcon fontSize="small" color="info" sx={{ ml: 0.5, verticalAlign: 'middle' }} /></span>
                                </Tooltip>
                              )}
                            </TableCell>
                          {task.category === 'Amprenta Personala' || task.category === 'Observatii Manager' ? (
                            // For Amprenta Personala or Observatii Manager, merge the last 4 columns and show only comments
                            <TableCell colSpan={4} sx={{ borderRight: '1px solid #e0e0e0', p: '2px' }}>
                              <TextField
                                multiline
                                size="small"
                                value={task.comments}
                                onChange={(e) => {
                                  setTasks(prevTasks => {
                                    const updatedTasks = prevTasks.map(t => 
                                      t.id === task.id ? { ...t, comments: e.target.value } : t
                                    );
                                    return updatedTasks;
                                  });
                                  setHasChanges(true);
                                }}
                                disabled={
                                  updating || isEditingDisabled(task) ||
                                  (task.category === 'Observatii Manager' && task.name === 'Observatii' && selectedUserId === currentUserId)
                                }
                                sx={{ 
                                  width: '100%',
                                  '& .MuiOutlinedInput-root': {
                                    fontSize: '12px',
                                    minHeight: '24px'
                                  },
                                  '& .MuiInputBase-input.Mui-disabled': {
                                    color: '#666666',
                                    WebkitTextFillColor: '#666666'
                                  }
                                }}
                                InputProps={{ 
                                  sx: { 
                                    fontSize: '12px',
                                    padding: '2px 4px'
                                  } 
                                }}
                                placeholder="Enter comments..."
                              />
                            </TableCell>
                          ) : (
                            // For other categories, show the normal 3 columns
                            <>
                              <TableCell sx={{ borderRight: '1px solid #e0e0e0', p: '2px' }}>
                                {availableTypes.length > 0 ? (
                                  <FormControl size="small" sx={{ 
                                    minWidth: 150,
                                    '& .MuiOutlinedInput-root': {
                                      height: '24px',
                                      fontSize: '12px'
                                    },
                                    '& .MuiInputBase-input.Mui-disabled': {
                                      color: '#666666',
                                      WebkitTextFillColor: '#666666'
                                    }
                                  }}>
                                    <Select
                                      value={task.type}
                                      onChange={async (e) => {
                                        const newType = e.target.value;
                                        
                                        // Update local state immediately for UI responsiveness
                                        setTasks(prevTasks => {
                                          const updatedTasks = prevTasks.map(t => 
                                            t.id === task.id ? { ...t, type: newType } : t
                                          );
                                          return updatedTasks;
                                        });
                                        
                                        // Save the change to backend using bulk save with type change flag
                                        try {
                                          // First save the individual task to ensure it's in the backend
                                          const formattedDate = format(selectedDate!, 'dd-MM-yyyy');
                                          const taskData = {
                                            task_date: formattedDate,
                                            category: task.category,
                                            name: task.name,
                                            type: newType,
                                            quantity: task.quantity,
                                            time: task.time,
                                            comments: task.comments,
                                          };
                                          
                                          await apiPost(`/task?userId=${selectedUserId}`, taskData);
                                          
                                          // Then refetch to get the updated data
                                          await fetchTasks(selectedDate!, false, selectedUserId ?? undefined);
                                        } catch (err: any) {
                                          setError(err.message);
                                          // Revert local state on error
                                          setTasks(prevTasks => {
                                            const revertedTasks = prevTasks.map(t => 
                                              t.id === task.id ? { ...t, type: task.type } : t
                                            );
                                            return revertedTasks;
                                          });
                                        }
                                      }}
                                      disabled={updating || task.type !== '' || isEditingDisabled(task)}
                                      displayEmpty
                                      inputProps={{
                                        sx: {
                                          fontSize: '12px',
                                          padding: '0px 4px'
                                        }
                                      }}
                                    >
                                      <MenuItem value="">
                                        <em>Select type</em>
                                      </MenuItem>
                                      {availableTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                          {type}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                ) : (
                                  <Box display="flex" alignItems="center">
                                    {task.type}
                                    {/* Show delete button for Altele tasks with type, only for current user */}
                                    {task.category === 'Volumetrie' && task.name === 'Altele' && task.type && task.userId === currentUserId && (
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => setDeleteTaskId(task.id)}
                                        sx={{ ml: 1 }}
                                        disabled={updating || isEditingDisabled(task)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell sx={{ borderRight: '1px solid #e0e0e0', p: '2px' }}>
                                {availableTypes.length === 0 || task.type !== '' ? (
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={task.quantity}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      const numValue = value === '' ? 0 : parseInt(value, 10) || 0;
                                      setTasks(prevTasks => {
                                        const updatedTasks = prevTasks.map(t => 
                                          t.id === task.id ? { ...t, quantity: numValue } : t
                                        );
                                        return updatedTasks;
                                      });
                                      // Only set hasChanges if the value actually changed
                                      if (numValue !== task.quantity) {
                                        setHasChanges(true);
                                      }
                                    }}
                                    onFocus={(e) => e.target.select()}
                                    disabled={
                                      updating || isEditingDisabled(task) ||
                                      (task.category === 'Volumetrie' && task.name === 'Lucrari noi introduse in CRM' && selectedUserId === currentUserId && (() => {
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        const selected = new Date(selectedDate!);
                                        selected.setHours(0, 0, 0, 0);
                                        return selected.getTime() === today.getTime();
                                      })())
                                    }
                                    sx={{ 
                                      width: 80,
                                      '& .MuiOutlinedInput-root': {
                                        height: '24px',
                                        fontSize: '12px'
                                      },
                                      '& .MuiInputBase-input.Mui-disabled': {
                                        color: '#666666',
                                        WebkitTextFillColor: '#666666'
                                      }
                                    }}
                                    InputProps={{ 
                                      sx: { 
                                        fontSize: '12px',
                                        height: '24px',
                                        padding: '0px 4px'
                                      } 
                                    }}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    Select type first
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ borderRight: '1px solid #e0e0e0', p: '2px' }}>
                                {availableTypes.length === 0 || task.type !== '' ? (
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={task.time}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      const numValue = value === '' ? 0 : parseInt(value, 10) || 0;
                                      setTasks(prevTasks => {
                                        const updatedTasks = prevTasks.map(t => 
                                          t.id === task.id ? { ...t, time: numValue } : t
                                        );
                                        return updatedTasks;
                                      });
                                      // Only set hasChanges if the value actually changed
                                      if (numValue !== task.time) {
                                        setHasChanges(true);
                                      }
                                    }}
                                    onFocus={(e) => e.target.select()}
                                    disabled={updating || isEditingDisabled(task)}
                                    sx={{ 
                                      width: 80,
                                      '& .MuiOutlinedInput-root': {
                                        height: '24px',
                                        fontSize: '12px'
                                      },
                                      '& .MuiInputBase-input.Mui-disabled': {
                                        color: '#666666',
                                        WebkitTextFillColor: '#666666'
                                      }
                                    }}
                                    InputProps={{ 
                                      sx: { 
                                        fontSize: '12px',
                                        height: '24px',
                                        padding: '0px 4px'
                                      } 
                                    }}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    Select type first
                                  </Typography>
                                )}
                              </TableCell>
                            </>
                          )}
                          {task.category !== 'Amprenta Personala' && task.category !== 'Observatii Manager' && (
                            <TableCell sx={{ p: '2px' }}>
                              {availableTypes.length === 0 || task.type !== '' ? (
                                <TextField
                                  multiline
                                  size="small"
                                  value={task.comments}
                                  onChange={(e) => {
                                    setTasks(prevTasks => {
                                      const updatedTasks = prevTasks.map(t => 
                                        t.id === task.id ? { ...t, comments: e.target.value } : t
                                      );
                                      return updatedTasks;
                                    });
                                    setHasChanges(true);
                                  }}
                                  disabled={updating || isEditingDisabled(task)}
                                  sx={{ 
                                    width: 150,
                                    '& .MuiOutlinedInput-root': {
                                      fontSize: '12px',
                                      minHeight: '24px'
                                    },
                                    '& .MuiInputBase-input.Mui-disabled': {
                                      color: '#666666',
                                      WebkitTextFillColor: '#666666'
                                    }
                                  }}
                                  InputProps={{ 
                                    sx: { 
                                      fontSize: '12px',
                                      padding: '2px 4px'
                                    } 
                                  }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  Select type first
                                </Typography>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    
                    {/* Manager Observations */}
                    {(() => {
                      const sortedObservations = sortManagerObservations(managerObservations);
                      return sortedObservations.map((task, index) => {
                        const key = `${task.category}-${task.name}-${task.id}`;
                        const isFirstObservationForUser = task.name === 'Observatii' && 
                          sortedObservations.findIndex(t => t.type === task.type && t.name === 'Observatii') === index;
                      
                      return (
                        <TableRow key={task.id}>
                          <TableCell sx={{ 
                            p: '2px', 
                            borderRight: '1px solid #e0e0e0',
                            fontWeight: 700,
                            backgroundColor: '#fff3e0' // Light orange for manager observations
                          }}>
                            {isFirstObservationForUser ? task.category : ''}
                          </TableCell>
                          <TableCell sx={{ 
                            p: '2px', 
                            borderRight: '1px solid #e0e0e0',
                            fontWeight: 700,
                            backgroundColor: '#fff3e0'
                          }}>
                            {task.name}
                            {task.name === 'Observatii' && (
                              (() => {
                                const userId = Number(task.type);
                                let username = '';
                                if (userId && observatiiUsernames[userId]) {
                                  username = observatiiUsernames[userId];
                                } else if (userId) {
                                  // Fetch username if not already fetched
                                  getUsername(userId).then(name => {
                                    setObservatiiUsernames(prev => ({ ...prev, [userId]: name }));
                                  });
                                  username = users.find(u => u.id === userId)?.username || '';
                                }
                                return username ? ` (${username})` : '';
                              })()
                            )}
                          </TableCell>
                          <TableCell colSpan={4} sx={{ borderRight: '1px solid #e0e0e0', p: '2px' }}>
                            <TextField
                              multiline
                              size="small"
                              value={task.comments}
                                                              onChange={(e) => {
                                  setManagerObservations(prevTasks => {
                                    const updatedTasks = prevTasks.map(t => 
                                      t.id === task.id ? { ...t, comments: e.target.value } : t
                                    );
                                    return updatedTasks;
                                  });
                                  setHasChanges(true);
                                }}
                                onBlur={async (e) => {
                                  // Send notification when Observatii or Raspunsuri field is left
                                  if ((task.name === 'Observatii' || task.name === 'Raspunsuri') && task.type && e.target.value.trim()) {
                                    try {
                                      const recipientId = parseInt(task.type);
                                      const currentUser = users.find(u => u.id === currentUserId);
                                      
                                      if (currentUser?.username && recipientId) {
                                        const message = task.name === 'Observatii' 
                                          ? `${currentUser.username} a adăugat o observație manager pentru ${format(selectedDate!, 'dd.MM.yyyy')}`
                                          : `${currentUser.username} a răspuns la observația manager pentru ${format(selectedDate!, 'dd.MM.yyyy')}`;
                                        
                                        const notificationData = {
                                          recipientId: task.name === 'Observatii' ? selectedUserId : recipientId,
                                          message: message,
                                          url: `/task-management?date=${format(selectedDate!, 'dd-MM-yyyy')}&userId=${selectedUserId}`,
                                        };
                                        
                                        await apiPost('/notification/send', notificationData);
                                      } 
                                    } catch (error) {
                                      console.error('Error sending notification:', error);
                                    }
                                  }
                                }}
                              disabled={updating || isEditingDisabled(task) || 
                                (task.name === 'Observatii' && parseInt(task.type) !== currentUserId) ||
                                (selectedUserId === currentUserId && task.name === 'Observatii') ||
                                (selectedUserId !== currentUserId && task.name === 'Raspunsuri')
                              }
                              sx={{ 
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                  fontSize: '12px',
                                  minHeight: '24px'
                                },
                                '& .MuiInputBase-input.Mui-disabled': {
                                  color: '#666666',
                                  WebkitTextFillColor: '#666666'
                                }
                              }}
                              InputProps={{ 
                                sx: { 
                                  fontSize: '12px',
                                  padding: '2px 4px'
                                } 
                              }}
                              placeholder="Enter comments..."
                            />
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                  </>
                  )}
                  {/* Summary row with total time */}
                  {tasks.length > 0 && (
                    <TableRow sx={{ backgroundColor: '#000', color: '#fff' }}>
                      <TableCell 
                        colSpan={3} 
                        sx={{ 
                          p: '2px', 
                          color: '#fff', 
                          fontWeight: 700,
                          borderTop: '2px solid #333'
                        }}
                      >
                        TOTAL TIMP
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          p: '2px', 
                          color: '#fff', 
                          fontWeight: 700,
                          borderTop: '2px solid #333',
                          borderRight: '1px solid #333'
                        }}
                      >
                        {totalMinutes} min
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          p: '2px', 
                          color: '#fff', 
                          fontWeight: 700,
                          borderTop: '2px solid #333',
                          borderRight: '1px solid #333'
                        }}
                      >
                        {timeDisplay}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          p: '2px', 
                          color: '#fff', 
                          fontWeight: 700,
                          borderTop: '2px solid #333'
                        }}
                      >
                        {/* Empty cell for comments column */}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* File Upload Section */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} mb={2}>
            Fișiere încărcate
          </Typography>
          
          {/* File Upload */}
          <Box sx={{ mb: 3 }}>
            <input
              accept="*/*"
              style={{ display: 'none' }}
              id="file-upload"
              type="file"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                disabled={uploading}
                sx={{ mb: 2 }}
              >
                {uploading ? 'Se încarcă...' : 'Încarcă fișier'}
              </Button>
            </label>
            
            {fileUploadError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {fileUploadError}
              </Alert>
            )}
          </Box>

          {/* File List */}
          {files.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              Nu există fișiere încărcate
            </Typography>
          ) : (
            <List>
              {files.map((file) => (
                <ListItem
                  key={file.id}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                >
                  <FileIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <ListItemText
                    primary={file.originalName}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(file.size)} • {format(new Date(file.createdAt), 'dd/MM/yyyy HH:mm')}
                        </Typography>
                        <Chip
                          label={file.mimeType}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box display="flex" gap={1}>
                      <IconButton
                        onClick={() => handleFileDownload(file.filename, file.originalName)}
                        size="small"
                        color="primary"
                      >
                        <DownloadIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleFileDelete(file.filename)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Change History Section */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Istoric Modificări
          </Typography>
          {changeHistoryLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : changeHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              Nu există modificări în ultimele 48 de ore pentru această dată.
            </Typography>
          ) : (
            <Box>
              {changeHistory.map((change) => (
                <Box key={change.id} sx={{ 
                  mb: 1, 
                  p: 1.5, 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  fontSize: '0.875rem'
                }}>
                  <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 500 }}>
                    {change.task?.category} - {change.task?.name}
                    {change.task?.type && ` (${change.task.type})`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                    {change.changedByUser?.username || `ID ${change.changedByUserId}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                    {change.fieldName === 'quantity' ? 'Cantitate' : 
                     change.fieldName === 'time' ? 'Timp' : 
                     change.fieldName === 'comments' ? 'Observații' : change.fieldName}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    backgroundColor: '#ffebee', 
                    px: 1, 
                    py: 0.25, 
                    borderRadius: 0.5,
                    textDecoration: 'line-through',
                    minWidth: 40,
                    textAlign: 'center'
                  }}>
                    {change.previousValue || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    backgroundColor: '#e8f5e8', 
                    px: 1, 
                    py: 0.25, 
                    borderRadius: 0.5,
                    fontWeight: 500,
                    minWidth: 40,
                    textAlign: 'center'
                  }}>
                    {change.newValue || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                    {format(new Date(change.createdAt), 'dd.MM HH:mm')}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={addTaskModalOpen} onClose={handleCloseAddTaskModal}>
        <DialogTitle>Adaugă task personalizat la Volumetrie / Altele</DialogTitle>
        <DialogContent>
          <TextField
            label="Nume task"
            value={newTaskName}
            onChange={e => setNewTaskName(e.target.value)}
            fullWidth
            margin="dense"
            autoFocus
          />
          {addTaskError && (
            <Alert severity="error" sx={{ mt: 1 }}>{addTaskError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddTaskModal}>Anulează</Button>
          <Button onClick={handleAddCustomTask} variant="contained">Adaugă</Button>
        </DialogActions>
      </Dialog>
      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTaskId} onClose={() => setDeleteTaskId(null)}>
        <DialogTitle>Confirmare ștergere</DialogTitle>
        <DialogContent>
          Sigur doriți să ștergeți acest task personalizat?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTaskId(null)} disabled={deleteLoading}>Anulează</Button>
          <Button onClick={handleDeleteTask} color="error" variant="contained" disabled={deleteLoading}>
            Șterge
          </Button>
        </DialogActions>
      </Dialog>

      {/* End Day Modal */}
      <Dialog open={endDayModalOpen} onClose={handleCloseEndDayModal} maxWidth="md" fullWidth>
        <DialogTitle>End Day - Întrebări de încheiere</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Question 1 */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                1. Toti clientii noi au fost introdusi in CRM
              </Typography>
              <RadioGroup
                value={endDayAnswers.crmIntroduced}
                onChange={(e) => setEndDayAnswers(prev => ({ ...prev, crmIntroduced: e.target.value }))}
                row
              >
                <FormControlLabel value="DA" control={<Radio />} label="DA" />
                <FormControlLabel value="NU" control={<Radio />} label="NU" />
              </RadioGroup>
              
              {endDayAnswers.crmIntroduced === 'DA' && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                      <DatePicker
                        label="Data pentru rezolvare"
                        value={endDayDates.crmIntroduced}
                        onChange={(date) => setEndDayDates(prev => ({ ...prev, crmIntroduced: date }))}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="Numărul de apariții"
                      type="number"
                      value={endDayOccurrences.crmIntroduced}
                      onChange={(e) => setEndDayOccurrences(prev => ({ ...prev, crmIntroduced: parseInt(e.target.value) || 0 }))}
                      size="small"
                      fullWidth
                    />
                  </Box>
                </Box>
              )}
            </Box>

            {/* Question 2 */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                2. Clienti noi neapelati
              </Typography>
              <RadioGroup
                value={endDayAnswers.newClientsCalled}
                onChange={(e) => setEndDayAnswers(prev => ({ ...prev, newClientsCalled: e.target.value }))}
                row
              >
                <FormControlLabel value="DA" control={<Radio />} label="DA" />
                <FormControlLabel value="NU" control={<Radio />} label="NU" />
              </RadioGroup>
              
              {endDayAnswers.newClientsCalled === 'DA' && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                      <DatePicker
                        label="Data pentru rezolvare"
                        value={endDayDates.newClientsCalled}
                        onChange={(date) => setEndDayDates(prev => ({ ...prev, newClientsCalled: date }))}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="Numărul de apariții"
                      type="number"
                      value={endDayOccurrences.newClientsCalled}
                      onChange={(e) => setEndDayOccurrences(prev => ({ ...prev, newClientsCalled: parseInt(e.target.value) || 0 }))}
                      size="small"
                      fullWidth
                    />
                  </Box>
                </Box>
              )}
            </Box>

            {/* Question 3 */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                3. Exista remindere expirate
              </Typography>
              <RadioGroup
                value={endDayAnswers.expiredReminders}
                onChange={(e) => setEndDayAnswers(prev => ({ ...prev, expiredReminders: e.target.value }))}
                row
              >
                <FormControlLabel value="DA" control={<Radio />} label="DA" />
                <FormControlLabel value="NU" control={<Radio />} label="NU" />
              </RadioGroup>
              
              {endDayAnswers.expiredReminders === 'DA' && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                      <DatePicker
                        label="Data pentru rezolvare"
                        value={endDayDates.expiredReminders}
                        onChange={(date) => setEndDayDates(prev => ({ ...prev, expiredReminders: date }))}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="Numărul de apariții"
                      type="number"
                      value={endDayOccurrences.expiredReminders}
                      onChange={(e) => setEndDayOccurrences(prev => ({ ...prev, expiredReminders: parseInt(e.target.value) || 0 }))}
                      size="small"
                      fullWidth
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEndDayModal} disabled={endDayLoading}>
            Anulează
          </Button>
          <Button 
            onClick={handleEndDaySubmit} 
            variant="contained" 
            disabled={endDayLoading}
            startIcon={endDayLoading ? <CircularProgress size={16} /> : null}
          >
            {endDayLoading ? 'Se procesează...' : 'Finalizează End Day'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Helper component for last updated info
const LastUpdatedInfo: React.FC<{
  tasks: Task[];
  users: User[];
  getUsername: (userId: number) => Promise<string>;
}> = ({ tasks, users, getUsername }) => {
  const [username, setUsername] = React.useState<string | null>(null);
  const [lastTime, setLastTime] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!tasks || tasks.length === 0) {
      setUsername(null);
      setLastTime(null);
      return;
    }
    // Find the task with the latest lastModifiedAt
    const latestTask = tasks.reduce((latest, curr) => {
      if (!curr.lastModifiedAt) return latest;
      if (!latest || (curr.lastModifiedAt > latest.lastModifiedAt!)) return curr;
      return latest;
    }, undefined as Task | undefined);
    if (latestTask && latestTask.lastModifiedAt && latestTask.lastModifiedBy) {
      setLastTime(latestTask.lastModifiedAt);
      // Try to get username from users list first
      const user = users.find(u => u.id === latestTask.lastModifiedBy);
      if (user) {
        setUsername(user.username);
      } else {
        // Fallback: fetch username
        getUsername(latestTask.lastModifiedBy).then(setUsername);
      }
    } else {
      setUsername(null);
      setLastTime(null);
    }
  }, [tasks, users, getUsername]);

  if (!lastTime || !username) return null;
  return (
    <Box mt={2} display="flex" justifyContent="flex-start">
      <Typography variant="body2" color="text.secondary">
        Ultima actualizare: {format(new Date(lastTime), 'dd.MM.yyyy HH:mm')} de {username}
      </Typography>
    </Box>
  );
};

export default TaskManagementPage;
